import { tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import type { ReactionNameEnum } from '../ReactionIcons';
import { ReactionSticker } from './ReactionSticker/ReactionSticker';
import { ReactionAvatar } from './ReactionAvatar';

const ContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
`;

// We use a smaller width than the content to make the sticker stacking each others
const OneStickerWrapper = styled.div<{ backgroundWidth: number }>`
  position: relative;
  width: ${({ backgroundWidth }) => backgroundWidth}px;
  display: flex;
  align-items: flex-end;
`;

const CounterDiv = styled.div`
  width: max-content;
  border-radius: 50%;
  background-color: #64b5f7;
  color: white;
  ${tokens.text('caption')}
  font-size: 10px;
  text-align: center;
  padding: 0 2px;
`;

const MAX_STICKER_NB = 2;

enum StickerKind {
  avatar,
  reaction,
  counter,
}

interface AvatarSticker {
  stickerKind: StickerKind.avatar;
  isMain: boolean;
  userName: string;
  src?: string;
}

interface ReactionSticker {
  stickerKind: StickerKind.reaction;
  isMain: boolean;
  reactionName: ReactionNameEnum;
}

interface CounterSticker {
  stickerKind: StickerKind.counter;
  count: number;
}

type Sticker = AvatarSticker | ReactionSticker | CounterSticker;

interface Author {
  name: string;
  avatar?: string;
}

interface Props {
  className?: string;
  commenters: Author[] | [];
  reactions: ReactionNameEnum[];
  onClick?: () => void;
  onMouseEnter?: () => void;
}

const computeStickerToShow = (
  commenters: Author[] | [],
  reactions: ReactionNameEnum[],
): Sticker[] => {
  const stickerList: Sticker[] = [];
  const commentersLeft = [...commenters];
  const reactionsLeft = [...reactions];

  // Show the first commenter in priority
  const hasCommenters = commentersLeft.length > 0;
  if (hasCommenters) {
    const { name, avatar } = commentersLeft[0];

    stickerList.push({
      stickerKind: StickerKind.avatar,
      isMain: true,
      userName: name,
      src: avatar,
    });
    // We remove this commenter from the arrray
    commentersLeft.shift();
  }

  // Show all the stickers until MAX_STICKER_NB
  if (reactionsLeft.length > 0 && stickerList.length < MAX_STICKER_NB) {
    const stickerNbToShow = Math.min(reactionsLeft.length, MAX_STICKER_NB - stickerList.length);
    // I want to loop with i++ because I'm shifting the array I'm working on
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < stickerNbToShow; i++) {
      const reaction = reactionsLeft[0];
      stickerList.push({
        stickerKind: StickerKind.reaction,
        isMain: !hasCommenters,
        reactionName: reaction,
      });
      // We remove this reaction from the arrray
      reactionsLeft.shift();
    }
  }

  // Show other commenters until MAX_STICKER_NB
  if (commentersLeft.length > 0 && stickerList.length < MAX_STICKER_NB) {
    const stickerNbToShow = MAX_STICKER_NB - stickerList.length;
    // I want to loop with i++ because I'm shifting the array I'm working on
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < stickerNbToShow; i++) {
      const { name, avatar } = commentersLeft[0];
      stickerList.push({
        stickerKind: StickerKind.avatar,
        isMain: false,
        userName: name,
        src: avatar,
      });
      // We remove this commenter from the arrray
      commentersLeft.shift();
    }
  }

  // Show counter
  if (reactionsLeft.length > 0 || commentersLeft.length > 0) {
    stickerList.push({
      stickerKind: StickerKind.counter,
      count: reactionsLeft.length + commentersLeft.length,
    });
  }

  return stickerList;
};

export const ReactionGroup = ({
  className,
  commenters,
  reactions,
  onClick,
  onMouseEnter,
}: Props): JSX.Element => {
  const stickerList = computeStickerToShow(commenters, reactions);

  return (
    <ContainerDiv className={className} onClick={onClick} onMouseEnter={onMouseEnter}>
      {stickerList.map((item, index) => {
        switch (item.stickerKind) {
          case StickerKind.avatar:
            return (
              // eslint-disable-next-line react/no-array-index-key
              <OneStickerWrapper key={index} backgroundWidth={item.isMain ? 26 : 14}>
                <ReactionAvatar
                  src={
                    !item.src || item.src.indexOf('default-avatar') !== -1 ? undefined : item.src
                  }
                  userName={item.userName}
                  size={item.isMain ? 'l' : 's'}
                  // FIXME [DXFE]: Fix css prop incompatibility
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  css={css`
                    position: absolute;
                  `}
                />
              </OneStickerWrapper>
            );
          case StickerKind.reaction:
            return (
              // eslint-disable-next-line react/no-array-index-key
              <OneStickerWrapper key={index} backgroundWidth={item.isMain ? 26 : 14}>
                <ReactionSticker
                  name={item.reactionName}
                  size={item.isMain ? 'l' : 'm'}
                  tiltRight={index % 2 !== 0}
                  // FIXME [DXFE]: Fix css prop incompatibility
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  css={css`
                    position: absolute;
                  `}
                />
              </OneStickerWrapper>
            );
          case StickerKind.counter:
            return (
              // eslint-disable-next-line react/no-array-index-key
              <OneStickerWrapper key={index} backgroundWidth={14}>
                <CounterDiv
                  css={css`
                    position: absolute;
                    bottom: -2px;
                  `}
                >
                  +{item.count}
                </CounterDiv>
              </OneStickerWrapper>
            );
          default:
            return <div />;
        }
      })}
    </ContainerDiv>
  );
};
